/**
 * Zod Schemas for Authentication Types
 *
 * Provides runtime validation schemas for Magic Link auth flow and session management.
 * These schemas enable validation of auth data at API boundaries.
 *
 * @module schemas/auth.zod
 * @see SPEC-004 Requirements 8.5
 */
import { z } from 'zod';
/**
 * Schema for Magic Link request payload.
 * Validates email format for magic link generation.
 */
export const MagicLinkRequestSchema = z.object({
    email: z.string().email('Invalid email format'),
});
/**
 * Schema for Magic Link verification request.
 * Validates token format (minimum 32 characters for security).
 */
export const VerifyTokenRequestSchema = z.object({
    token: z.string().min(32, 'Invalid token format'),
});
/**
 * Schema for successful authentication response.
 * Contains session token and expiration timestamp.
 */
export const AuthResponseSchema = z.object({
    sessionToken: z.string().min(1),
    expiresAt: z.number().int().positive(),
    userId: z.string().uuid('Invalid userId format'),
    email: z.string().email('Invalid email format'),
});
/**
 * Schema for session data stored in Redis.
 * Contains user identity, tenant isolation, and session lifecycle timestamps.
 *
 * @see SPEC-007 Requirements 2.6, 9.6 - Tenant Isolation
 */
export const SessionDataSchema = z.object({
    userId: z.string().uuid('Invalid userId format'),
    email: z.string().email('Invalid email format'),
    /**
     * Tenant ID for multi-tenant isolation.
     * Ensures users can only access resources within their tenant.
     * @default 'default'
     * @see SPEC-007 Requirements 2.6, 9.6
     */
    tenantId: z.string().min(1).default('default'),
    createdAt: z.number().int().positive(),
    expiresAt: z.number().int().positive(),
    lastAccessedAt: z.number().int().positive(),
    /**
     * Admin flag for privileged operations.
     * Only set via /__diag/test-session in staging environments.
     * @default false
     * @see SPEC-015 Requirement 2.9 - Admin-only operations
     */
    isAdmin: z.boolean().optional().default(false),
});
//# sourceMappingURL=auth.zod.js.map